import { Product } from '@shared/components/ui/Product';
import { faCartShopping, faLocationDot } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useProductDetails } from '../../hooks/useProductDetail';
import { useLocationDetails } from '../../hooks/useLocationDetail';
import type { ProductDetail, RelocationLine, RelocationTotal } from '../../models';
import { MovedProductItemSkeleton } from '../skeletons/MovedProductItemSkeleton';
import { RelocationMovedLineLot } from './RelocationMovedLineLot';

interface MovedProductsPanelProps {
  lines: RelocationLine[];
  totals?: RelocationTotal[];
  cartLabel?: string;
}

interface EnrichedLine extends RelocationLine {
  product_detail?: ProductDetail;
  origin_location_label?: string;
}

export const MovedProductsPanel: React.FC<MovedProductsPanelProps> = ({ lines, totals = [], cartLabel }) => {
  const { t } = useTranslation('relocationProcessModule');
  const sanitizeText = (value?: string | null) => {
    if (!value) return undefined;
    const trimmed = value.trim();
    if (!trimmed || trimmed.toLowerCase() === 'undefined' || trimmed.toLowerCase() === 'null') {
      return undefined;
    }
    return trimmed;
  };

  const productIds = useMemo(() => {
    const lineIds = lines.map((l) => l.product_id).filter(Boolean);
    const totalIds = totals.map((t) => t.product_id).filter(Boolean);
    return [...lineIds, ...totalIds];
  }, [lines, totals]);
  const { productsById, isLoading } = useProductDetails(productIds);
  const locationIds = useMemo(
    () => lines.map((line) => line.from_location_id || line.location_id || 0).filter(Boolean),
    [lines],
  );
  const { locationsById, isLoading: isLoadingLocations } = useLocationDetails(locationIds);

  const enrichedLines: EnrichedLine[] = useMemo(() => {
    return lines.map((line) => ({
      ...line,
      product_detail: productsById.get(line.product_id),
      origin_location_label:
        sanitizeText(line.location?.code) ||
        sanitizeText(locationsById.get(line.from_location_id || line.location_id || 0)?.code) ||
        (line.from_location_id ? `#${line.from_location_id}` : undefined) ||
        (line.location_id ? `#${line.location_id}` : undefined),
    }));
  }, [lines, locationsById, productsById]);

  const hasLines = enrichedLines.length > 0;

  return (
    <div className="flex-1 lg:pl-8 min-h-0 flex flex-col">
      <div className="flex items-center justify-end lg:justify-between mb-2 lg:mb-4 flex-shrink-0">
        <h2 className="hidden lg:flex text-lg font-semibold text-foreground items-center space-x-2">
          <span className="text-orange-600 dark:text-orange-400">{t('panel.moved_title')}</span>
          <span className="text-sm text-muted-foreground">({enrichedLines.length})</span>
        </h2>
      </div>

      <div className="flex-1 overflow-y-auto">
        {hasLines ? (
          <div className="space-y-4">
            {enrichedLines.map((line) => {
              const detail = line.product_detail;
              const fallbackImage = line.product?.image || line.product?.image_hash || '';
              const fallbackName = sanitizeText(line.product?.name);
              const fallbackCode = sanitizeText(line.product?.code);
              const shouldShowSkeleton = (!detail && isLoading) || (isLoadingLocations && !line.origin_location_label);

              if (shouldShowSkeleton) {
                return <MovedProductItemSkeleton key={`line-skeleton-${line.id}`} />;
              }

              const productId = Number.isInteger(line.product_id) ? line.product_id : 0;
              const name = sanitizeText(detail?.name) || fallbackName;
              const code = sanitizeText(detail?.code) || fallbackCode;
              const image = detail?.image_hash || fallbackImage;
              const hasQty = typeof line.qty === 'number' && Number.isFinite(line.qty);

              // Si la línea no tiene datos mínimos de producto, no renderizamos nada
              // para evitar mostrar "undefined" en la UI.
              if (!productId || !name) {
                return null;
              }

              return (
                <div
                  key={line.id}
                  className="border-b pb-4 last:border-b-0 hover:bg-muted/50 bg-background border-border"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <Product
                        id={productId}
                        src={image}
                        name={name}
                        code={code}
                        acronym={name?.charAt(0).toUpperCase()}
                        showImageModal={true}
                        enableLineClamp={true}
                      />
                    </div>

                    <div className="flex flex-col items-end space-y-1 ml-4">
                      {hasQty && (
                        <div className="flex items-center space-x-2">
                          <span className="text-lg font-semibold text-foreground">{line.qty}</span>
                          <span className="text-xs text-muted-foreground">{t('panel.moved_qty')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <RelocationMovedLineLot line={line} />
                  {line.origin_location_label && (
                    <div className="mt-1.5 inline-flex items-center gap-3 text-xs text-muted-foreground">
                      {cartLabel && (
                        <span className="inline-flex items-center gap-1.5">
                          <FontAwesomeIcon icon={faCartShopping} className="w-3.5 h-3.5" />
                          <span>{cartLabel}</span>
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1.5">
                        <FontAwesomeIcon icon={faLocationDot} className="w-3.5 h-3.5" />
                        <span className="opacity-80">{t('panel.origin_location')}:</span>
                        <span>{line.origin_location_label}</span>
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bg-muted/30 border-b border-border/50 p-6 rounded-lg">
            <div className="flex items-center justify-center text-orange-600 dark:text-orange-400">
              <span className="text-sm font-medium">{t('panel.empty')}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
